import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import {
  Plus, Globe, Trash2, AlertTriangle, Info, Briefcase, ArrowRight, ShieldAlert, CheckCircle2,
} from "lucide-react";

// CS 21 — Wholesalers: HO-managed list, single-currency at create, link-status pill.
export default function Wholesalers() {
  const { user: me } = useAuth();
  const canEdit = me?.role === "master_admin";
  const [list, setList] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState({
    name: "", country: "", currency: "USD", pin: "1234",
  });
  const [deleting, setDeleting] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    () => api.get("/wholesalers").then((r) => setList(r.data?.items || [])),
    []
  );
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!creating.name.trim()) return toast.error("Name is required");
    if (!/^\d{4,8}$/.test(creating.pin)) return toast.error("PIN must be 4-8 digits");
    try {
      await api.post("/wholesalers", {
        name: creating.name.trim(),
        country: creating.country.trim(),
        cost_currency: creating.currency,
        selling_currency: creating.currency,
        fx_rate_inr_per_usd: null,
        pin: creating.pin,
      });
      toast.success("Wholesaler created");
      setShowCreate(false);
      setCreating({ name: "", country: "", currency: "USD", pin: "1234" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Create failed");
    }
  };

  const openDelete = (w) => {
    setDeleting(w);
    setConfirmText("");
  };
  const confirmDelete = async () => {
    if (!deleting) return;
    if (confirmText.trim() !== deleting.name) return toast.error("Name does not match");
    setBusy(true);
    try {
      await api.delete(`/wholesalers/${deleting.id}`);
      toast.success(`Deleted ${deleting.name}`);
      setDeleting(null);
      setConfirmText("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="wholesalers-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#C9A84C] mb-2">Network</p>
          <h1 className="font-serif text-4xl md:text-5xl text-[#0D1B2A]">Wholesalers</h1>
          <p className="text-slate-500 mt-2">
            Manage wholesale clients with HO-owned cost rate-cards and client-owned selling rate-cards.
          </p>
        </div>
        {canEdit && (
          <Button
            onClick={() => setShowCreate(true)}
            className="bg-[#C9A84C] hover:bg-[#C9A84C]/90 text-white"
            data-testid="create-wholesaler-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Add wholesaler
          </Button>
        )}
      </div>

      {!canEdit && (
        <div className="luxe-card p-4 bg-amber-50/50 border-l-4 border-amber-300 flex items-start gap-3" data-testid="wholesalers-readonly-banner">
          <Info className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <p className="text-sm text-amber-800">Viewing wholesaler list (read-only). Contact a Master Admin to modify.</p>
        </div>
      )}

      <div className="grid gap-6">
        {list === null && Array.from({ length: 2 }).map((_, i) => <div key={`skel-${i}`} className="skeleton h-48" />)}
        {list && list.length === 0 && (
          <div className="luxe-card p-10 text-center text-slate-400" data-testid="wholesalers-empty">
            <Briefcase className="w-10 h-10 mx-auto mb-3 text-slate-300" strokeWidth={1.5} />
            <p>No wholesalers yet.</p>
            {canEdit && <p className="text-xs mt-2">Click &quot;Add wholesaler&quot; to create your first one.</p>}
          </div>
        )}

        {list && list.map((w) => {
          const linkReady = w.link_ready;
          const missing = w.link_block_reasons || [];
          return (
            <div key={w.id} className="luxe-card p-6 space-y-5" data-testid={`wholesaler-${w.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4 pb-4 border-b border-[#0D1B2A]/5">
                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-serif text-2xl text-[#0D1B2A]">{w.name}</h3>
                    <span className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full ${
                      w.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"
                    }`}>
                      {w.status || "active"}
                    </span>
                    <span className="text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full bg-[#0D1B2A]/5 text-[#0D1B2A]/70 font-mono">
                      {w.cost_currency}
                    </span>
                  </div>
                  <div className="text-sm text-slate-500 mt-1">
                    {[w.country, `Created ${(w.created_at || "").slice(0, 10)}`].filter(Boolean).join(" · ")}
                  </div>
                </div>

                <div className="flex flex-col items-end gap-2">
                  {linkReady ? (
                    <span
                      data-testid={`link-ready-${w.id}`}
                      className="flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" /> Link ready
                    </span>
                  ) : (
                    <span
                      data-testid={`link-blocked-${w.id}`}
                      className="flex items-center gap-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full"
                      title={`Missing: ${missing.join(", ")}`}
                    >
                      <ShieldAlert className="w-3.5 h-3.5" /> Cost card incomplete
                    </span>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <RouterLink
                  to={`/wholesalers/${w.id}`}
                  data-testid={`open-wholesaler-${w.id}`}
                  className="px-3 py-2 rounded-md border border-[#0D1B2A]/15 text-sm flex items-center gap-2 hover:bg-[#FAF8F3]"
                >
                  <ArrowRight className="w-4 h-4" /> Open
                </RouterLink>
                {linkReady && (
                  <RouterLink
                    to={`/wholesalers/${w.id}?tab=link`}
                    data-testid={`open-wholesaler-link-${w.id}`}
                    className="px-3 py-2 rounded-md border border-[#0D1B2A]/15 text-sm flex items-center gap-2 hover:bg-[#FAF8F3]"
                  >
                    <Globe className="w-4 h-4" /> Link &amp; PIN
                  </RouterLink>
                )}
                {canEdit && (
                  <button
                    onClick={() => openDelete(w)}
                    data-testid={`delete-wholesaler-${w.id}`}
                    className="px-3 py-2 rounded-md border border-red-300 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 ml-auto"
                  >
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">New wholesaler</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Name</label>
              <Input
                placeholder="e.g. Ruchi Jewels"
                value={creating.name}
                onChange={(e) => setCreating({ ...creating, name: e.target.value })}
                data-testid="create-wholesaler-name"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Country</label>
              <Input
                placeholder="e.g. USA"
                value={creating.country}
                onChange={(e) => setCreating({ ...creating, country: e.target.value })}
                data-testid="create-wholesaler-country"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">
                Currency (governs both cost &amp; selling)
              </label>
              <Select
                value={creating.currency}
                onValueChange={(v) => setCreating({ ...creating, currency: v })}
              >
                <SelectTrigger className="w-full" data-testid="create-wholesaler-currency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD" data-testid="create-wholesaler-currency-usd">USD ($)</SelectItem>
                  <SelectItem value="INR" data-testid="create-wholesaler-currency-inr">INR (₹)</SelectItem>
                  <SelectItem value="GBP" data-testid="create-wholesaler-currency-gbp">GBP (£)</SelectItem>
                  <SelectItem value="EUR" data-testid="create-wholesaler-currency-eur">EUR (€)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[10px] text-slate-400 mt-1">
                One currency for both lanes. Multi-currency (USD cost / INR sell with FX) is a future enhancement.
              </p>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">
                Access PIN (4-8 digits)
              </label>
              <Input
                value={creating.pin}
                onChange={(e) => setCreating({ ...creating, pin: e.target.value.replace(/\D/g, "").slice(0, 8) })}
                maxLength={8}
                inputMode="numeric"
                className="font-mono text-center tracking-[0.5em]"
                data-testid="create-wholesaler-pin"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button
              onClick={create}
              className="bg-[#0D1B2A] text-white hover:bg-[#0D1B2A]/90"
              data-testid="create-wholesaler-submit"
            >Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={!!deleting} onOpenChange={(o) => { if (!o && !busy) { setDeleting(null); setConfirmText(""); } }}>
        <DialogContent className="bg-white max-w-lg" data-testid="delete-wholesaler-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-6 h-6" /> Delete wholesaler
            </DialogTitle>
          </DialogHeader>
          {deleting && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Deleting <span className="font-semibold text-[#0D1B2A]">{deleting.name}</span> will permanently remove
                all of its stock, shipments, cost &amp; selling settings, diamond rate-card, and gold-rate history.
                This cannot be undone.
              </p>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={deleting.name}
                autoFocus
                data-testid="delete-wholesaler-confirm-input"
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleting(null); setConfirmText(""); }} disabled={busy}>Cancel</Button>
            <Button
              onClick={confirmDelete}
              disabled={busy || confirmText.trim() !== deleting?.name}
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
              data-testid="delete-wholesaler-confirm-btn"
            >
              {busy ? "Deleting…" : "Delete wholesaler"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
